import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useRecoilValue } from 'recoil';
import SimpleIconAction, { TABLE_DETAIL_ACTION } from '../CmdTable/TableActions/SimpleIconAction';
import { IVehicleCustomerCell, IVehicleUserCell, VehicleRecord } from './types';
import {
  VEHICLE_SETTINGS_TABLE_KEY,
  VEHICLE_SETTINGS_CUSTOMER_ID_KEY,
  VEHICLE_SETTINGS_CREATED_BY,
  VEHICLE_SETTINGS_CREATED_AT,
  VEHICLE_SETTINGS_DETAIL_KEY,
  VEHICLE_SETTINGS_LICENCE_NUMBER,
  VEHICLE_SETTINGS_MANUFACTURER,
  VEHICLE_SETTINGS_NAME,
  VEHICLE_SETTINGS_TYPE,
  VEHICLE_SETTINGS_DRIVER,
  VEHICLE_SETTINGS_FUEL_CARD,
  VEHICLE_SETTINGS_COST_CENTER,
  VEHICLE_SETTINGS_FUEL_TYPE,
  VEHICLE_SETTINGS_CAPACITY,
  VEHICLE_SETTINGS_CONS_THEORETICAL,
  VEHICLE_SETTINGS_CONS_NORM,
  VEHICLE_SETTINGS_INIT_MILEAGE,
  VEHICLE_SETTINGS_UNIT_MODEL,
  VEHICLE_SETTINGS_PHONE,
  VEHICLE_SETTINGS_CAN,
  VEHICLE_SETTINGS_PRIVATE_RIDE_DEFAULT,
  VEHICLE_SETTINGS_PRIVATE_RIDE_INPUT,
  VEHICLE_SETTINGS_AUTO_CORR,
  VEHICLE_SETTINGS_ECO_DRIVE,
  VEHICLE_SETTINGS_MODEL,
  VEHICLE_SETTINGS_MANUFACTURER_YAER,
  VEHICLE_SETTINGS_COMMISSIONING,
  VEHICLE_SETTINGS_VIN,
  VEHICLE_SETTINGS_TECH_VALID,
  VEHICLE_SETTINGS_EMISSION_VALID,
  VEHICLE_SETTINGS_NOTE,
  VEHICLE_SETTINGS_MODIFIED_BY,
  VEHICLE_SETTINGS_MODIFIED_AT,
  VEHICLE_SETTINGS_CARD_READER,
  VEHICLE_SETTINGS_BLOCKING,
  VEHICLE_SETTINGS_BLUECOIN,
} from './constants';
import { TABLE_VEHICLE } from '../../Services/TableService';
import { URL_VEHICLES_SETTINGS } from '../../router/constants';
import { ICmdTableColumn } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import IconCell from '../CmdTable/TableCells/IconCell';
import ChipsCellWithModal from './SpecificCell/ChipsCellWithModal';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import ThRange from '../CmdTable/TableHeadCell/ThRange';
import DetailAction from '../CmdTable/TableActions/DetailAction';
import { columnsAtom } from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';
import { getTableName } from '../CmdTable/CmdTableService';

interface IUseVehicleSettings {
  columns: ColumnDef<VehicleRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useVehicleSettings(): IUseVehicleSettings {
  const { formatMessage: f } = useIntl();
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const { trackEvent } = useAnalytics();

  const handleDetail = (id: number, customerId?: number | number[]) => {
    trackEvent([`table ${getTableName(VEHICLE_SETTINGS_TABLE_KEY)}`, 'click', 'Open vehicle detail']);
    if (customerId && !Array.isArray(customerId)) {
      navigate(`${URL_VEHICLES_SETTINGS}/edit/${id}?customerId=${customerId}`);
    }
  };

  const renderFuelCards = (data, id) => {
    if (Array.isArray(data)) {
      const text = data[0].provider
        ? `${data[0].provider} - ${data[0].cardNumber}`
        : data[0].cardNumber;
      return data.length > 1 ? (
        <ChipsCellWithModal data={data} id={id} align="left" />
      ) : (
        <TextCell text={text} />
      );
    }
    return <TextCell text="" />;
  };

  const columns = React.useMemo<ColumnDef<VehicleRecord>[]>(
    () => [
      {
        accessorKey: VEHICLE_SETTINGS_CUSTOMER_ID_KEY,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.customerId` })}
            tooltip={f({ id: 'tables.tripsOverview.header.tooltip.customer' })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.customer ? (
            <CustomerCell customer={info.getValue<IVehicleCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 160,
      },
      {
        accessorKey: VEHICLE_SETTINGS_LICENCE_NUMBER,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_LICENCE_NUMBER}
            title={f({ id: `table_list.columns.vehicles.licenceNumber` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_LICENCE_NUMBER}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.licenseNumber ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_MANUFACTURER,
        header: (table) => (
          <ThSearch
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_MANUFACTURER}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_MANUFACTURER}` })}
            // tooltip={f({ id: `table_list.columns.importTemplates.${PARKING_TAG_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.manufacturer ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_NAME,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_NAME}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_NAME}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_VEHICLE_SETTINGS_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicleName ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_TYPE,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_TYPE}
            title={f({ id: `table_list.columns.vehicleTypeId` })}
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_TYPE}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicleType ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_DRIVER,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_DRIVER}
            title={f({ id: `table_list.columns.driverId` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_DRIVER}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicleType ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_FUEL_CARD,
        header: (table) => (
          <ThOnlySort
            id={VEHICLE_SETTINGS_FUEL_CARD}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_FUEL_CARD}` })}
            // tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_DATE_FROM_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.fuelCards ? (
            renderFuelCards(info.row.original.fuelCards, info.row.original.id)
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_COST_CENTER,
        header: (table) => (
          <ThMultiSelect
            id={VEHICLE_SETTINGS_COST_CENTER}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.costCenterId` })}
            // tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_DATE_TO_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.costCenter ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_FUEL_TYPE,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_FUEL_TYPE}
            title={f({ id: `table_list.columns.fuelTypeId` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_FUEL_TYPE}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicleType ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_CAPACITY,
        header: (table) => (
          <ThRange
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CAPACITY}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CAPACITY}` })}
            unit="L"
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CAPACITY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.totalCapacity ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_CONS_THEORETICAL,
        header: (table) => (
          <ThRange
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CONS_THEORETICAL}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CONS_THEORETICAL}` })}
            unit="L/100km"
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CONS_THEORETICAL}.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.cons100kmTheoretical ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_CONS_NORM,
        header: (table) => (
          <ThRange
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CONS_NORM}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CONS_NORM}` })}
            unit="L/100km"
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CONS_NORM}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.cons100kmNorm ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_INIT_MILEAGE,
        header: (table) => (
          <ThOnlySort
            id={VEHICLE_SETTINGS_INIT_MILEAGE}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_INIT_MILEAGE}` })}
            defaultTooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_INIT_MILEAGE}.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.initialMileage ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_UNIT_MODEL,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_UNIT_MODEL}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_UNIT_MODEL}Id` })}
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_UNIT_MODEL}.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.unitModel ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_PHONE,
        header: (table) => (
          <ThSearch
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_PHONE}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_PHONE}` })}
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_PHONE}.tooltip` })}
            isSortable={false}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.phoneNumber ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_CAN,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CAN}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CAN}` })}
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CAN}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.can ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_PRIVATE_RIDE_DEFAULT,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_PRIVATE_RIDE_DEFAULT}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_PRIVATE_RIDE_DEFAULT}` })}
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_PRIVATE_RIDE_DEFAULT}.tooltip`,
            })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          !info.row.original?.privateRideDefault ? (
            <IconCell
              isIcon={true}
              icon="text-gray-lighten fs-4 cmd-icons cmd-icon-ride-mode-work"
              tooltip={f({ id: 'enum.ridebooks.privacy.business' })}
            />
          ) : (
            <IconCell
              isIcon={true}
              icon="text-gray-lighten fs-4 cmd-icons cmd-icon-ride-mode-private"
              tooltip={f({ id: 'enum.ridebooks.privacy.private' })}
            />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_PRIVATE_RIDE_INPUT,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_PRIVATE_RIDE_INPUT}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_PRIVATE_RIDE_INPUT}` })}
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_PRIVATE_RIDE_INPUT}.tooltip`,
            })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.privateRideInput ? (
            <IconCell
              isIcon={info.getValue<boolean>()}
              icon="cmd-icon-active text-success"
              tooltip={f({ id: 'enum.status.available' })}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_AUTO_CORR,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_AUTO_CORR}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_AUTO_CORR}` })}
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_AUTO_CORR}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.autocorrectionEnabled ? (
            <IconCell
              isIcon={info.getValue<boolean>()}
              icon="cmd-icon-active text-success"
              tooltip={f({ id: 'enum.status.enabledF' })}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_ECO_DRIVE,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_ECO_DRIVE}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_ECO_DRIVE}` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_ECO_DRIVE}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.ecoDrive ? (
            <IconCell
              isIcon={info.getValue<boolean>()}
              icon="cmd-icon-active text-success"
              tooltip={f({ id: 'enum.status.enabledM' })}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_MODEL,
        header: (table) => (
          <ThSearch
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_MODEL}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_MODEL}` })}
            // tooltip={f({ id: `table_list.columns.importTemplates.${PARKING_TAG_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.model ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_MANUFACTURER_YAER,
        header: (table) => (
          <ThRange
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_MANUFACTURER_YAER}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_MANUFACTURER_YAER}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_VEHICLE_SETTINGS_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.manufactureYear ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_COMMISSIONING,
        header: (table) => (
          <ThDateTime
            id={VEHICLE_SETTINGS_COMMISSIONING}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_COMMISSIONING}` })}
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_COMMISSIONING}.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.commissioning ? (
            <DateTimeCell datetime={info.getValue<string | null>()} format="D.M.YYYY" />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_VIN,
        header: (table) => (
          <ThSearch
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_VIN}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_VIN}` })}
            tooltip={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_VIN}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.vin ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_TECH_VALID,
        header: (table) => (
          <ThDateTime
            id={VEHICLE_SETTINGS_TECH_VALID}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_TECH_VALID}` })}
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_TECH_VALID}.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.technicalInspectionValidTo ? (
            <DateTimeCell datetime={info.getValue<string | null>()} format="D.M.YYYY" />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_EMISSION_VALID,
        header: (table) => (
          <ThDateTime
            id={VEHICLE_SETTINGS_EMISSION_VALID}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_EMISSION_VALID}` })}
            tooltip={f({
              id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_EMISSION_VALID}.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.emissionInspectionValidTo ? (
            <DateTimeCell datetime={info.getValue<string | null>()} format="D.M.YYYY" />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_NOTE,
        header: (table) => (
          <ThSearch
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_NOTE}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_NOTE}` })}
            // tooltip={f({ id: `table_list.columns.importTemplates.${PARKING_TAG_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.note ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_CARD_READER,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CARD_READER}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_CARD_READER}` })}
            tooltip={f({
              id: `vehicleSettings.cardReaderType.tooltip`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.cardReaderType ? (
            <TextCell text={info.getValue<string>().toLocaleUpperCase()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_BLOCKING,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_BLOCKING}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_BLOCKING}` })}
            tooltip={f({ id: `vehicleSettings.vehicleBlockingAvailable.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicleBlockingAvailable ? (
            <IconCell
              isIcon={info.getValue<boolean>()}
              icon="cmd-icon-active text-success"
              tooltip={f({ id: 'vehicleSettings.availableF' })}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_BLUECOIN,
        header: (table) => (
          <ThSimpleSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_BLUECOIN}
            title={f({ id: `table_list.columns.vehicles.${VEHICLE_SETTINGS_BLUECOIN}` })}
            tooltip={f({ id: `vehicleSettings.bluecoinEnabled.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.bluecoinEnabled ? (
            <IconCell
              isIcon={info.getValue<boolean>()}
              icon="cmd-icon-active text-success"
              tooltip={f({ id: `vehicleSettings.enabledM` })}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: VEHICLE_SETTINGS_CREATED_BY,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_CREATED_BY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_CREATED_BY}` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<IVehicleUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_CREATED_AT,
        header: (table) => (
          <ThDateTime
            id={VEHICLE_SETTINGS_CREATED_AT}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_CREATED_AT}` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_CREATED_AT}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_MODIFIED_BY,
        header: (table) => (
          <ThMultiSelect
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            id={VEHICLE_SETTINGS_MODIFIED_BY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_MODIFIED_BY}` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_MODIFIED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<IVehicleUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_SETTINGS_MODIFIED_AT,
        header: (table) => (
          <ThDateTime
            id={VEHICLE_SETTINGS_MODIFIED_AT}
            tableName={VEHICLE_SETTINGS_TABLE_KEY}
            title={f({ id: `table_list.columns.${VEHICLE_SETTINGS_MODIFIED_AT}` })}
            tooltip={f({ id: `table_list.columns.${VEHICLE_SETTINGS_MODIFIED_AT}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: VEHICLE_SETTINGS_DETAIL_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DetailAction
              id={info.row.original.id}
              tableName={TABLE_VEHICLE}
              customerId={info.row.original.customer.id}
              onClickCallback={handleDetail}
            />
          ) : null,
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    // order.push(VEHICLE_SETTINGS_ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
